import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Grid, Card, CardContent, Divider, Chip, CircularProgress } from '@mui/material';
import NightlightRoundIcon from '@mui/icons-material/NightlightRound';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { get } from '../../../services/api-services';
import { formatDatecase } from '../../../modules/ConvertDate';

const MetricRow = ({ label, value, color, showDivider = true }) => (
    <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" pb={1.5}>
            <Typography variant="caption" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#aaa' : '#555', fontSize: '0.75rem' }}>{label}</Typography>
            <Typography variant="body2" fontWeight="500" sx={{ color: color || ((theme) => theme.palette.text.primary), fontSize: '0.85rem' }}>{value}</Typography>
        </Box>
        {showDivider && <Divider sx={{ borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }} />}
    </Box>
);

const TrendBar = ({ value, label, active }) => (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
        <Typography variant="caption" sx={{ color: active ? '#3c50b4' : '#444', fontSize: '0.65rem', fontWeight: active ? 'bold' : 'normal' }}>{value}</Typography>
        <Box sx={{ 
            width: '6px', 
            height: '45px', 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5', 
            borderRadius: '10px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <Box sx={{ 
                position: 'absolute', bottom: 0, width: '100%', 
                height: active ? '75%' : '45%', 
                backgroundColor: (theme) => active ? '#3c50b4' : (theme.palette.mode === 'dark' ? '#333' : '#e0e0e0'),
                borderRadius: '10px'
            }} />
        </Box>
        <Typography variant="caption" sx={{ color: '#444', fontSize: '0.65rem' }}>{label}</Typography>
    </Box>
);

export default function HealthMetrics({ userId, userData }) {
    const isFemale = userData?.gender?.toLowerCase() === 'female';
    const [sleepLogs, setSleepLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const mHistory = userData?.menstrualHistory || {};
    
    // Calculate Menstrual Health Metrics
    const calculateMenstrualData = () => {
        // Support both nested menstrualHistory and top-level fallbacks
        const lastPhase = mHistory.lastMenstrualPhase || userData?.periodDate;
        const cycleLen = mHistory.avarageCycleLength || userData?.cycleLength;
        const periodDur = mHistory.periodFlowLength || userData?.periodDuration;

        if (!lastPhase) return null;

        const lastDate = new Date(lastPhase);
        const cycleLengthValue = parseInt(cycleLen) || 28;
        const durationValue = parseInt(periodDur) || 5;

        const nextExpectedDate = new Date(lastDate);
        nextExpectedDate.setDate(lastDate.getDate() + cycleLengthValue);

        const today = new Date();
        const diffTime = nextExpectedDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return {
            lastPeriod: formatDatecase(lastPhase),
            cycleLength: `${cycleLengthValue} Days`,
            periodDuration: `${durationValue} Days`,
            nextExpected: formatDatecase(nextExpectedDate.toISOString()),
            daysRemaining: diffDays > 0 ? `${diffDays} days` : 'Overdue',
            status: mHistory.regular === 'false' ? 'Irregular' : 'On Track'
        };
    };

    const menstrualData = calculateMenstrualData();

    const fetchSleepLogs = useCallback(async () => {
        try {
            const url = `/Customers/${userId}/sleepLogs?filter={"order":"date DESC","limit":7}`;
            const res = await get(url);
            if (res?.statusCode === 200) {
                setSleepLogs(res.data);
            }
        } catch (error) {
            console.error("Error fetching sleep logs:", error);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (userId) {
            fetchSleepLogs();
        }
    }, [userId, fetchSleepLogs]);

    const lastLog = sleepLogs[0];
    const avgSleep = sleepLogs.length > 0 
        ? (sleepLogs.reduce((acc, log) => acc + (parseFloat(log.hours) || 0), 0) / sleepLogs.length).toFixed(1)
        : '0';

    const getTrendData = () => {
        const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
        const today = new Date();
        const trend = [];
        
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(today.getDate() - i);
            d.setHours(0,0,0,0);
            
            const log = sleepLogs.find(l => {
                const logDate = new Date(l.date);
                logDate.setHours(0,0,0,0);
                return logDate.getTime() === d.getTime();
            });
            
            trend.push({
                label: days[d.getDay()],
                value: log ? `${log.hours}h` : '-',
                active: i === 0,
                hours: log ? parseFloat(log.hours) : 0
            });
        }
        return trend;
    };

    const sleepData = {
        lastNight: lastLog ? `${lastLog.hours} hours` : 'No data',
        quality: lastLog ? lastLog.quality : 'No data',
        average: `${avgSleep} hours`,
        consistency: sleepLogs.length > 3 ? 'Medium' : 'Low',
        lastLogged: lastLog ? formatDatecase(lastLog.date) : 'Never'
    };

    // Calculate Alerts
    const getAlerts = () => {
        const alerts = [];
        // Sleep below 6h for 3 consecutive days
        let consecutiveLowSleep = 0;
        for (let log of sleepLogs) {
            if (parseFloat(log.hours) < 6) {
                consecutiveLowSleep++;
            } else {
                break;
            }
        }
        if (consecutiveLowSleep >= 3) {
            alerts.push({
                type: 'error',
                message: 'Sleep below 6h for 3 consecutive days',
                color: '#d32f2f'
            });
        }

        // Sleep consistency
        if (sleepLogs.length > 3 && sleepData.consistency === 'Low') {
            alerts.push({
                type: 'info',
                message: 'Sleep consistency is lower.',
                color: '#3c50b4'
            });
        }

        return alerts;
    };

    const alerts = getAlerts();

    if (loading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

    return (
        <Box sx={{ mt: 2, color: (theme) => theme.palette.text.primary }}>
            {/* Health Summary Container */}
            <Card sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#0a0a0a' : '#fff', border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: '12px', mb: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" mb={3} sx={{ color: (theme) => theme.palette.text.primary, fontSize: '0.9rem' }}>Health Summary</Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={isFemale ? 6 : 12}>
                            <Box display="flex" alignItems="center" gap={2.5}>
                                <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: 'rgba(121, 85, 72, 0.1)', color: '#8d6e63' }}>
                                    <NightlightRoundIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>Sleep Status</Typography>
                                    <Typography variant="body2" fontWeight="bold" sx={{ color: sleepData.quality === 'Poor' ? '#ff9800' : '#4db6ac', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {sleepData.quality} {sleepData.quality === 'Poor' && <ErrorOutlineIcon sx={{ fontSize: 16, color: '#ff9800' }} />}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        {isFemale && (
                            <Grid item xs={12} md={6}>
                                <Box display="flex" alignItems="center" gap={2.5}>
                                    <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: 'rgba(0, 150, 136, 0.1)', color: '#009688' }}>
                                        <CalendarMonthIcon sx={{ fontSize: 28 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>Menstrual Status</Typography>
                                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#009688', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {menstrualData?.status || 'No Data'} {menstrualData?.status === 'On Track' && <CheckCircleIcon sx={{ fontSize: 16, color: '#009688' }} />}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            {/* Details Section */}
            <Grid container spacing={3} mb={4}>
                {/* Sleep Details */}
                <Grid item xs={12} md={isFemale ? 6 : 12}>
                    <Card sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#0a0a0a' : '#fff', border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: '16px', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <CardContent sx={{ p: 3.5, flex: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.95rem', color: (theme) => theme.palette.text.primary }}>Sleep</Typography>
                                <Chip 
                                    label={sleepData.quality} 
                                    size="small" 
                                    sx={{ 
                                        backgroundColor: sleepData.quality === 'Poor' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(76, 175, 80, 0.1)', 
                                        color: sleepData.quality === 'Poor' ? '#e57373' : '#81c784', 
                                        fontSize: '0.65rem', height: 18, borderRadius: '4px' 
                                    }} 
                                 />
                            </Box>
                            
                            <MetricRow label="Last Night Sleep" value={sleepData.lastNight} />
                            <MetricRow label="Sleep Quality" value={sleepData.quality} />
                            <MetricRow label="7 - Day Average" value={sleepData.average} />
                            <MetricRow label="Consistency" value={sleepData.consistency} color="#7986cb" />
                            <MetricRow label="Last Logged" value={sleepData.lastLogged} showDivider={false} />
                            
                            <Box mt={5}>
                                <Box display="flex" alignItems="center" gap={1} mb={3}>
                                    <TrendingUpIcon sx={{ fontSize: 16, color: '#444' }} />
                                    <Typography variant="caption" sx={{ color: '#444', fontWeight: 'bold' }}>Last 7 Days Trend</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" px={1}>
                                    {getTrendData().map((t, idx) => (
                                        <TrendBar key={idx} label={t.label} value={t.value} active={t.active} />
                                    ))}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Menstrual Details */}
                {isFemale && (
                    <Grid item xs={12} md={6}>
                        <Card sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#0a0a0a' : '#fff', border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: '16px', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ p: 3.5, flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.95rem', color: (theme) => theme.palette.text.primary }}>Menstrual Health</Typography>
                                    <Chip 
                                        label={menstrualData?.status || 'No Data'} 
                                        size="small" 
                                        sx={{ 
                                            backgroundColor: menstrualData?.status === 'On Track' ? 'rgba(0, 150, 136, 0.1)' : 'rgba(255, 152, 0, 0.1)', 
                                            color: menstrualData?.status === 'On Track' ? '#4db6ac' : '#ffb74d', 
                                            fontSize: '0.65rem', height: 18, borderRadius: '4px' 
                                        }} 
                                    />
                                </Box>
                                
                                <MetricRow label="Last Period Date" value={menstrualData?.lastPeriod || 'No data'} />
                                <MetricRow label="Cycle Length" value={menstrualData?.cycleLength || 'No data'} />
                                <MetricRow label="Period Duration" value={menstrualData?.periodDuration || 'No data'} />
                                <MetricRow label="Next Expected Date" value={menstrualData?.nextExpected || 'No data'} color="#7986cb" />
                                <MetricRow label="Days Remaining" value={menstrualData?.daysRemaining || 'No data'} showDivider={false} />
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>

            {/* Alerts Container */}
            <Card sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#0a0a0a' : '#fff', border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                        <ErrorOutlineIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.9rem', color: (theme) => theme.palette.text.primary }}>Alerts</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {alerts.length > 0 ? alerts.map((alert, idx) => (
                            <Box key={idx} sx={{ 
                                backgroundColor: `${alert.color}08`, 
                                border: `1px solid ${alert.color}26`, 
                                borderLeft: `4px solid ${alert.color}`,
                                borderRadius: '4px', 
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}>
                                {alert.type === 'error' ? <ErrorOutlineIcon sx={{ color: alert.color, fontSize: 18 }} /> : <InfoOutlinedIcon sx={{ color: alert.color, fontSize: 18 }} />}
                                <Typography variant="caption" sx={{ color: alert.color, fontWeight: '500' }}>{alert.message}</Typography>
                            </Box>
                        )) : (
                            <Typography variant="caption" sx={{ color: '#666', textAlign: 'center', py: 2 }}>No active health alerts</Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
